import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, Check, Trash2, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { isPushSupported, pushPermission, subscribeToPush } from '../utils/webPush';

// Real Chuy avatar — uses the public chuy.png asset
const ChuyAvatar = () => (
  <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-rosa/20 to-gold/20 shrink-0 shadow-inner ring-2 ring-caribbean/30">
    <img
      src="/chuy.png"
      alt="Chuy"
      className="w-full h-full object-cover object-top"
      loading="lazy"
    />
  </div>
);

const POLL_MS = 20000;

function timeAgo(iso, lang) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === 'en' ? 'just now' : 'ahora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const { api, user, token } = useAuth();
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pushStatus, setPushStatus] = useState(() => {
    if (typeof window === 'undefined') return 'default';
    if (!isPushSupported()) return 'unsupported';
    return pushPermission();
  }); // 'default'|'granted'|'denied'|'unsupported'
  const [enabling, setEnabling] = useState(false);
  const panelRef = useRef(null);
  const buttonRef = useRef(null);

  const enablePush = async () => {
    setEnabling(true);
    try {
      const res = await subscribeToPush({ token, language });
      if (res.ok) {
        setPushStatus('granted');
        toast.success(language === 'en' ? 'Notifications enabled' : 'Notificaciones activadas');
      } else if (res.reason === 'denied') {
        setPushStatus('denied');
        toast.error(language === 'en'
          ? 'You blocked notifications. Enable them in Settings.'
          : 'Bloqueaste las notificaciones. Actívalas en Ajustes.');
      } else if (res.reason === 'ios-not-pwa') {
        toast.error(language === 'en'
          ? 'On iPhone: Share → Add to Home Screen, then open BidTrip from that icon to enable push.'
          : 'En iPhone: Compartir → Agregar a Inicio, luego abre BidTrip desde el ícono para activar.',
          { duration: 8000 });
      } else if (res.reason === 'unsupported') {
        toast.error(language === 'en'
          ? 'This browser does not support push notifications.'
          : 'Este navegador no soporta notificaciones push.');
      } else {
        toast.error((language === 'en'
          ? 'Could not enable notifications. '
          : 'No se pudo activar las notificaciones. ') + (res.detail || res.reason || ''));
      }
    } finally {
      setEnabling(false);
    }
  };

  const sendTestPush = async () => {
    try {
      let { data } = await api().post('/notifications/test-push');
      // If the backend has no record of this device (e.g. the original
      // subscribe call failed silently in an older build), try to re-register
      // it now and retry the test push exactly once.
      if (data?.reason === 'no_subscription' && pushPermission() === 'granted') {
        const res = await subscribeToPush({ token, language });
        if (res?.ok) {
          ({ data } = await api().post('/notifications/test-push'));
        } else {
          toast.error(language === 'en'
            ? 'Could not register this device. Reload the app and tap Enable again.'
            : 'No se pudo registrar este dispositivo. Recarga la app y toca Activar de nuevo.');
          return;
        }
      }
      if (data?.sent) {
        toast.success(language === 'en'
          ? `Test sent to ${data.succeeded || data.subscriptions} device(s)`
          : `Prueba enviada a ${data.succeeded || data.subscriptions} dispositivo(s)`);
      } else if (data?.attempts?.length) {
        // We have rich diagnostics — show the first failure reason to the user.
        const first = data.attempts.find((a) => !a.ok) || data.attempts[0];
        const status = first?.status ? `HTTP ${first.status}` : '';
        const host = first?.host ? ` · ${first.host}` : '';
        const reason = first?.reason || first?.err || 'failed';
        toast.error(
          (language === 'en' ? 'Push delivery failed. ' : 'Falló el envío push. ')
          + `${status} ${host} (${reason})`.trim(),
          { duration: 8000 }
        );
        console.error('[push] test-push diagnostics:', data.attempts);
      } else {
        const reason = data?.reason || 'failed';
        const map = {
          no_subscription: language === 'en' ? 'No registered devices. Enable push first.' : 'No hay dispositivos registrados. Activa push primero.',
          vapid_missing: language === 'en' ? 'Server VAPID keys missing.' : 'Faltan llaves VAPID en el servidor.',
          send_failed: language === 'en' ? 'Send failed. Check server logs.' : 'Falló el envío. Revisa logs.',
        };
        toast.error(map[reason] || reason);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const fetchNotifs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api().get('/notifications?limit=30');
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // silent
    }
  }, [api, user]);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      await fetchNotifs();
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [fetchNotifs]);

  // Auto-resync push subscription with backend when the browser already has
  // permission "granted" but the backend may have lost the subscription record
  // (happens if the very first subscribe call failed silently in older builds).
  // Runs once per login.
  useEffect(() => {
    if (!user || !token) return;
    if (!isPushSupported()) return;
    if (pushPermission() !== 'granted') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await subscribeToPush({ token, language });
        if (!cancelled && res?.ok) {
          setPushStatus('granted');
        }
      } catch { /* no-op */ }
    })();
    return () => { cancelled = true; };
  }, [user, token, language]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpenItem = async (n) => {
    if (!n.read) {
      try { await api().post(`/notifications/${n.id}/read`); } catch { /* no-op */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try { await api().post('/notifications/read-all'); } catch { /* no-op */ }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  const removeOne = async (e, id) => {
    e.stopPropagation();
    try { await api().delete(`/notifications/${id}`); } catch { /* no-op */ }
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        className="relative w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rosa text-white text-[10px] font-bold flex items-center justify-center shadow-md"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Mobile backdrop */}
          <div className="fixed inset-0 z-40 bg-black/20 md:hidden" />
          <div
            ref={panelRef}
            data-testid="notification-panel"
            className="fixed left-2 right-2 mt-2 md:absolute md:left-auto md:right-0 md:w-[360px] rounded-2xl border border-border bg-card shadow-2xl z-50 overflow-hidden"
            style={{ top: '3.5rem' }}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <p className="font-heading font-bold text-sm">
                {language === 'en' ? 'Notifications' : 'Notificaciones'}
              </p>
              <div className="flex items-center gap-2">
                {items.length > 0 && (
                  <button
                    onClick={markAllRead}
                    data-testid="notif-mark-all-read"
                    className="text-[11px] text-caribbean font-semibold inline-flex items-center gap-1 hover:underline"
                  >
                    <Check size={12} />
                    {language === 'en' ? 'Mark all read' : 'Marcar leídas'}
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="w-7 h-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {/* Push permission CTA */}
              {pushStatus === 'default' && (
                <div className="px-4 py-3 bg-caribbean/10 border-b border-caribbean/30">
                  <div className="flex items-start gap-2.5">
                    <BellRing size={18} className="text-caribbean shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-foreground">
                        {language === 'en' ? 'Get notified anywhere' : 'Recibe avisos donde sea'}
                      </p>
                      <p className="text-[11px] text-muted-foreground leading-snug mt-0.5">
                        {language === 'en'
                          ? 'Enable push so Chuy can ping you even when the app is closed.'
                          : 'Activa push para que Chuy te avise aunque cierres la app.'}
                      </p>
                      <button
                        onClick={enablePush}
                        disabled={enabling}
                        data-testid="enable-push-btn"
                        className="mt-2 inline-flex items-center gap-1 rounded-full bg-caribbean hover:bg-caribbean/90 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
                      >
                        {enabling
                          ? (language === 'en' ? 'Enabling…' : 'Activando…')
                          : (language === 'en' ? 'Enable push' : 'Activar push')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {pushStatus === 'granted' && (
                <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 dark:border-emerald-800 flex items-center justify-between gap-2">
                  <p className="text-[11px] text-emerald-700 dark:text-emerald-300">
                    {language === 'en' ? 'Push enabled on this device' : 'Push activado en este dispositivo'}
                  </p>
                  <button
                    type="button"
                    onClick={sendTestPush}
                    data-testid="send-test-push-btn"
                    className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold px-2.5 py-1"
                  >
                    <Send size={11} />
                    {language === 'en' ? 'Send test' : 'Enviar prueba'}
                  </button>
                </div>
              )}
              {pushStatus === 'denied' && (
                <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-300">
                  {language === 'en'
                    ? 'Push notifications are blocked. Enable them in your browser/iOS settings.'
                    : 'Las notificaciones están bloqueadas. Actívalas en ajustes del navegador/iOS.'}
                </div>
              )}
              {pushStatus === 'unsupported' && (
                <div className="px-4 py-2 bg-muted/50 border-b border-border text-[11px] text-muted-foreground">
                  {language === 'en'
                    ? 'For lock-screen notifications: add BidTrip to your home screen (iOS 16.4+).'
                    : 'Para notificaciones en pantalla bloqueada: agrega BidTrip a tu pantalla de inicio (iOS 16.4+).'}
                </div>
              )}

              {items.length === 0 ? (
                <div className="px-4 py-10 text-center text-muted-foreground text-sm">
                  <Bell className="mx-auto mb-2 opacity-50" size={28} />
                  {language === 'en'
                    ? "All clear — no notifications yet"
                    : 'Todo en orden — sin notificaciones aún'}
                </div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleOpenItem(n)}
                    data-testid={`notif-${n.id}`}
                    className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-border/60 hover:bg-muted/40 transition-colors ${
                      !n.read ? 'bg-caribbean/5' : ''
                    }`}
                  >
                    <ChuyAvatar />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-tight ${!n.read ? 'font-bold' : 'font-semibold'}`}>
                        {language === 'en' ? n.title_en : n.title_es}
                      </p>
                      <p className="text-[12px] text-muted-foreground leading-snug mt-0.5 line-clamp-3">
                        {language === 'en' ? n.body_en : n.body_es}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {timeAgo(n.created_at, language)}
                      </p>
                    </div>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-rosa mt-2 shrink-0" aria-hidden />
                    )}
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => removeOne(e, n.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') removeOne(e, n.id); }}
                      className="w-7 h-7 rounded-full hover:bg-destructive/15 text-destructive flex items-center justify-center shrink-0 cursor-pointer"
                      aria-label="Delete"
                    >
                      <Trash2 size={12} />
                    </span>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
